import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import Table from 'components/v2/Table';
import Checkbox from 'components/v2/forms/Checkbox';

const TripPlannerEquipmentMatrix = ({ onChangeData, equipments, legs, t }) => {
  const [activeEquipment, setActiveEquipment] = useState(
    equipments && equipments.length ? equipments[0].equipment_id : null,
  );

  const generateMatrixData = (equipments, legs) => {
    const matrixData = {};
    const generateLegsData = (eqId) => {
      const legRes = {};
      legs.forEach((leg) => {
        const stopData = {};
        leg.stops.forEach((stop, i) => {
          const eqIdArr = stop.equipments.map((eq) => eq.equipment_id);
          stopData[`stop_${i + 1}`] = eqIdArr.includes(eqId);
        });

        legRes[`leg_${leg.sequence}`] = stopData;
      });

      return legRes;
    };

    equipments
      .map((eq) => eq.equipment_id)
      .forEach((eqId) => {
        matrixData[eqId] = generateLegsData(eqId);
      });

    return matrixData;
  };

  const renderEquipments = () => {
    return equipments.map((equipment) => {
      const { equipment_id: equipmentId } = equipment;
      const isActive = equipmentId === activeEquipment;
      const className = classnames({
        'cursor-pointer': true,
        rounded: true,
        'bg-white': true,
        border: true,
        'border-grey': true,
        'hover:border-green': true,
        'border-green': isActive,
        'border-transparent': !isActive,
        'p-3': true,
        'mr-3': true,
        'text-sm': true,
        'text-black': isActive,
        'bg-green-400': isActive,
      });

      return (
        <button
          onClick={() => setActiveEquipment(equipmentId)}
          className={className}
        >
          {equipmentId}
        </button>
      );
    });
  };

  const updateOneStop = (matrixData, eqId, legIndex, stopIndex) => {
    const legIndexStr = `leg_${legIndex + 1}`;
    const stopIndexStr = `stop_${stopIndex + 1}`;
    const temp = {
      ...matrixData,
      [eqId]: {
        ...matrixData[eqId],
        [legIndexStr]: {
          ...matrixData[eqId][legIndexStr],
          [stopIndexStr]: !matrixData[eqId][legIndexStr][stopIndexStr],
        },
      },
    };

    onChangeData(temp);
  };

  const updateAllStopsInEquipment = (matrixData, eqId, checked) => {
    const newEqData = {};
    Object.keys(matrixData[eqId]).forEach((leg) => {
      const updatedStops = {};
      Object.keys(matrixData[eqId][leg]).forEach((stopId) => {
        updatedStops[stopId] = !checked;
      });
      newEqData[leg] = {
        ...updatedStops,
      };
    });

    const temp = {
      ...matrixData,
      [eqId]: {
        ...newEqData,
      },
    };

    onChangeData(temp);
  };

  const updateAllStopsInLeg = (matrixData, eqId, legIndex, checked) => {
    const legIndexStr = `leg_${legIndex + 1}`;
    const updatedLeg = {};
    Object.keys(matrixData[eqId][legIndexStr]).forEach((stopId) => {
      updatedLeg[stopId] = !checked;
    });

    const temp = {
      ...matrixData,
      [eqId]: {
        ...matrixData[eqId],
        [legIndexStr]: {
          ...updatedLeg,
        },
      },
    };

    onChangeData(temp);
  };

  const renderMatrix = () => {
    const data = generateMatrixData(equipments, legs);
    const longest = Object.values(Object.values(data)[0]).reduce((a, b) =>
      Object.values(a).length > Object.values(b).length ? a : b,
    );
    const activeData = data[activeEquipment];

    const setHeaders = () => {
      const stopsHeader = Object.values(longest).map((leg, i) => {
        return {
          field: `stop_${i + 1}`,
          label: `${t('STOP')} ${i + 1}`,
        };
      });

      return [
        {
          field: 'leg',
          label: t('LEG'),
        },
        {
          field: 'selectAll',
          label: '',
          renderHeader: () => {
            const checked = Object.keys(activeData).every((legKey) =>
              Object.keys(activeData[legKey]).every((stopKey) =>
                Boolean(activeData[legKey][stopKey]),
              ),
            );

            return (
              <Checkbox
                checked={checked}
                onChange={() =>
                  updateAllStopsInEquipment(data, activeEquipment, checked)
                }
                id="selectAll"
              />
            );
          },
        },
        ...stopsHeader,
      ];
    };

    const setColumns = () => {
      const stopsHeader = Object.values(longest).map((leg, i) => {
        return {
          field: `stop_${i + 1}`,
          label: `${t('STOP')} ${i + 1}`,
        };
      });

      return [
        {
          field: 'leg',
          label: t('LEG'),
        },
        {
          field: 'selectAll',
          label: t(''),
        },
        ...stopsHeader,
      ];
    };

    const setData = () => {
      return Object.values(activeData).map((stops, legIndex) => {
        const renderStops = {};
        Object.values(stops).forEach((stop, stopIndex) => {
          renderStops[`stop_${stopIndex + 1}`] = (
            <Checkbox
              checked={stop}
              onChange={() =>
                updateOneStop(data, activeEquipment, legIndex, stopIndex)
              }
              id="selectAll"
            />
          );
        });

        const legChecked = Object.values(
          activeData[`leg_${legIndex + 1}`],
        ).every((stop) => Boolean(stop));

        return {
          leg: `${t('LEG')} ${legIndex + 1}`,
          selectAll: (
            <Checkbox
              checked={legChecked}
              onChange={() =>
                updateAllStopsInLeg(data, activeEquipment, legIndex, legChecked)
              }
              id="selectAll"
            />
          ),
          ...renderStops,
        };
      });
    };

    return (
      <Table headers={setHeaders()} columns={setColumns()} data={setData()} />
    );
  };

  return (
    <>
      <div className="flex flex-wrap mb-4">{renderEquipments()}</div>
      <div className="mb-4">{renderMatrix()}</div>
    </>
  );
};

export default withTranslation()(TripPlannerEquipmentMatrix);
